import "./App.css";
import Login from "./Pages/Login/Login";
import Signup from "./Pages/Signup/Signup";
import DashboardProfile from "./Pages/DashboardProfile/DashboardProfile";
import ChangePassword from "./Pages/ChangePassword/ChangePassword";
import ForgetPasswordNew from "./Pages/ForgetPassword/ForgetPasswordNew";
import ResetPassword from "./Pages/ResetPassword/ResetPassword";
import UserBulletinBoard from "./Pages/UserBulletinBoard";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import ScrollToTop from "./Components/ScrollToTop";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PageNotFound from "./Pages/PageNotFound";
import Loader from "./Components/Loader";
import { useReduxStore } from "./hooks/useReduxStore";
import { ToastContainer, toast } from "react-toastify";
import useSetWidth from "./hooks/useSetWidth";
import { stripeAPIKey } from "./Constant/Data";
import Pricing from "./Pages/Pricing/Pricing";
import Forecast from "./Pages/Forecast/Forecast";
import MainLayout from "./Pages/MainLayout/MainLayout";
import DashboardHome from "./Pages/DashboardHome/DashboardHome";
import Layers from "./Pages/Layers/Layers";
import ForecastInner from "./Pages/ForecastInner/ForecastInner";
import ForecastDashboard from "./Pages/ForecastDashboard/ForecastDashboard";
import Radar from "./Pages/Radar/Radar.jsx";
import LatestMapsGl from "./Pages/LatestMapsGl/LatestMapsGl.jsx";
import UserSubscription from "./Pages/UserSubscription/UserSubscription";
import ApiKeys from "./Pages/ApiKeys/ApiKeys";
import AlertsList from "./Pages/AlertsList/AlertsList";
import SupportList from "./Pages/Admin/Support/SupportList";
import SupportDetail from "./Pages/Admin/Support/SupportDetail";
import BulletinBoard from "./Pages/BulletinBoard";
import BulletinDetail from "./Pages/BulletinDetail";

// admin pages----------------------
import AdminLogin from "./Pages/Login/AdminLogin";
import AdminLayout from "./Pages/Admin/AdminLayout/AdminLayout";
import AdminDashboard from "./Pages/Admin/AdminDashboard/AdminDashboard";
import SubscriptionManagement from "./Pages/Admin/SubscriptionManagement/SubscriptionManagement";
import SubscriptionDetail from "./Pages/Admin/SubscriptionDetail/SubscriptionDetail";
import ApiKeysManagement from "./Pages/Admin/ApiKeysManagement/ApiKeysManagement";
import ApiKeyDetail from "./Pages/Admin/ApiKeyDetail/ApiKeyDetail";
import UserManagement from "./Pages/Admin/UserManagement/UserManagement";
import UserDetail from "./Pages/Admin/UserDetail/UserDetail";
import AdminAlerts from "./Pages/Admin/Alerts/Alerts";
import Settings from "./Pages/Admin/Settings/Settings";
import AdminChat from "./Pages/Admin/AdminChat/AdminChat";
import DashboardPage from "./Pages/DashboardPage.jsx";
import UserChatPage from "./Pages/UserChatPage.jsx";
import { SocketProvider } from "./contexts/SocketContext";
import { AuthProvider } from "./contexts/AuthContext";
import { WeatherLocationProvider } from "./contexts/WeatherLocationContext";

const stripePromise = loadStripe(stripeAPIKey);
function App() {
  useSetWidth();
  const { getState } = useReduxStore();
  const { loading, token } = getState("AUTH");
  const { adminToken } = getState("ADMIN_AUTH");
  const { dashBoardData } = getState("Home") || {};
  const { subscription: authSubscriptions } = getState("AUTH") || {};
  const currentSubscription =
    dashBoardData?.subscription ||
    (Array.isArray(authSubscriptions) ? authSubscriptions[0] : null);
  const planName = (currentSubscription?.planId?.name || "Free")?.toLowerCase();
  const isFreePlan = planName === "free";

  return (
    <Elements stripe={stripePromise}>
      <Router future={{ v7_startTransition: true }}>
        <WeatherLocationProvider>
        <ScrollToTop />
        <ToastContainer
          position="bottom-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          draggable
          theme="light"
        />
        <ToastContainer />

        {loading && <Loader />}
        <Routes>
          <Route
            path="/"
            element={!token ? <Login /> : <Navigate to={"/Dashboard"} />}
          />
          <Route
            path="/Signup"
            element={!token ? <Signup /> : <Navigate to={"/Dashboard"} />}
          ></Route>
          <Route
            path="/Login"
            element={!token ? <Login /> : <Navigate to={"/Dashboard"} />}
          ></Route>
          <Route
            path="/Admin/Login"
            element={
              !adminToken ? (
                <AdminLogin />
              ) : (
                <Navigate to={"/Admin/Dashboard"} />
              )
            }
          ></Route>

          <Route path="/Pricing" element={<Pricing />}></Route>
          <Route path="/pricing" element={<Navigate to="/Pricing" replace />} />
          <Route
            path="/Forecast"
            element={
              isFreePlan ? (
                <Navigate to="/Pricing" replace />
              ) : (
                <Forecast />
              )
            }
          ></Route>

          <Route
            path="/ChangePassword/:token"
            element={<ChangePassword />}
          ></Route>
          <Route
            path="/forgot-password"
            element={
              !token ? (
                <ForgetPasswordNew />
              ) : (
                <Navigate to={"/Dashboard"} />
              )
            }
          ></Route>
          <Route
            path="/reset-password"
            element={
              !token ? <ResetPassword /> : <Navigate to={"/Dashboard"} />
            }
          ></Route>

          <Route
            path="/Dashboard"
            element={token ? <MainLayout /> : <Navigate to={"/"} />}
          >
            <Route index element={<DashboardHome />}></Route>
            <Route path="/Dashboard/Layers" element={<Layers />}></Route>
            <Route path="/Dashboard/Forecast" element={<ForecastDashboard />} />
            <Route path="/Dashboard/Radar" element={<Radar />} />
            <Route
              path="/Dashboard/Profile"
              element={<DashboardProfile />}
            ></Route>
            <Route
              path="/Dashboard/Subscription"
              element={<UserSubscription />}
            ></Route>
            <Route path="/Dashboard/APIKeys" element={<ApiKeys />}></Route>
            <Route path="/Dashboard/Alerts" element={<AlertsList />} />
            <Route path="/Dashboard/Support" element={<SupportList />}></Route>
            <Route
              path="/Dashboard/Chat"
              element={
                <SocketProvider>
                  <UserChatPage />
                </SocketProvider>
              }
            />
            <Route
              path="/Dashboard/BulletinBoard"
              element={<UserBulletinBoard />}
            />
            <Route
              path="/Dashboard/BulletinBoard/:id"
              element={<BulletinDetail />}
            />
          </Route>
          <Route
            path="/Admin/Dashboard"
            element={
              adminToken ? <AdminLayout /> : <Navigate to={"/Admin/Login"} />
            }
          >
            <Route index element={<AdminDashboard />}></Route>
            <Route
              path="/Admin/Dashboard/Users"
              element={<UserManagement />}
            ></Route>
            <Route
              path="/Admin/Dashboard/Users/Detail"
              element={<UserDetail />}
            ></Route>
            <Route
              path="/Admin/Dashboard/APIKeys"
              element={<ApiKeysManagement />}
            ></Route>
            <Route
              path="/Admin/Dashboard/APIKeys/Detail"
              element={<ApiKeyDetail />}
            ></Route>
            <Route
              path="/Admin/Dashboard/BulletinBoard"
              element={<BulletinBoard />}
            ></Route>
            <Route
              path="/Admin/Dashboard/Subscription"
              element={<SubscriptionManagement />}
            ></Route>
            <Route
              path="/Admin/Dashboard/Subscription/Detail"
              element={<SubscriptionDetail />}
            ></Route>
            <Route
              path="/Admin/Dashboard/Support"
              element={<SupportList />}
            ></Route>
            <Route
              path="/Admin/Dashboard/Support/Detail"
              element={<SupportDetail />}
            ></Route>
            <Route path="/Admin/Dashboard/Alerts" element={<AdminAlerts />}></Route>
            <Route path="/Admin/Dashboard/Settings" element={<Settings />}></Route>
            <Route
              path="/Admin/Dashboard/Chat"
              element={
                <SocketProvider>
                  <DashboardPage />
                </SocketProvider>
              }
            ></Route>
          </Route>
        </Routes>
        </WeatherLocationProvider>
      </Router>
    </Elements>
  );
}

export default App;
